/**
 * Post Detail Page — single post shown exactly like the feed (reuses the feed's
 * PostCard), with ALL actions: Like, Comment, Reshare, Share, Save, Delete.
 * Reached by clicking a post tile on the profile (Posts + Saved tabs) or via
 * a shared post link. Self-contained handlers + comment modal mirror the feed
 * so behaviour matches what the user sees on /feed. Additive — the feed page is
 * untouched (PostCard is merely exported from it).
 */
import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import postService, { Post } from '@/services/postService'
import GovLayout from '@/components/layout/GovLayout'
import { PostCard } from '@/pages/feed/PremiumFeedPage'
import { Loader2, X, MessageCircle, ArrowLeft, Flag } from 'lucide-react'
import ReportModal from '@/components/common/ReportModal'

const PostDetailPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [post, setPost] = useState<Post | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  // Comment modal state (mirrors the feed's modal)
  const [commentPostId, setCommentPostId] = useState<number | null>(null)
  const [commentText, setCommentText] = useState('')
  const [comments, setComments] = useState<any[]>([])
  const [loadingComments, setLoadingComments] = useState(false)
  const [submittingComment, setSubmittingComment] = useState(false)
  const [reportPostId, setReportPostId] = useState<number | null>(null)

  useEffect(() => {
    let mounted = true
    const load = async () => {
      setLoading(true)
      setNotFound(false)
      try {
        // postService.getPost returns the ApiResponse wrapper (response.data);
        // the post object lives at its `.data` field.
        const res = await postService.getPost(Number(id))
        if (mounted) {
          if (res?.success && res.data) setPost(res.data)
          else setNotFound(true)
        }
      } catch (e) {
        console.error('Error fetching post:', e)
        if (mounted) setNotFound(true)
      }
      if (mounted) setLoading(false)
    }
    if (id) load()
    return () => { mounted = false }
  }, [id])

  const handleReaction = useCallback(async (postId: number) => {
    try {
      await postService.react(postId, 'LIKE')
    } catch (e) {
      console.error(e)
    }
  }, [])

  const handleSave = useCallback(async (postId: number) => {
    try {
      await postService.toggleSave(postId)
    } catch (e) {
      console.error(e)
    }
  }, [])

  const handleOpenComments = useCallback(async (postId: number) => {
    setCommentPostId(postId)
    setLoadingComments(true)
    setComments([])
    try {
      const res = await postService.getComments(postId, 0, 20)
      setComments(res.data?.content || res.data || [])
    } catch (e) {
      console.error(e)
    }
    setLoadingComments(false)
  }, [])

  const handleSubmitComment = useCallback(async () => {
    if (!commentPostId || !commentText.trim()) return
    setSubmittingComment(true)
    try {
      await postService.addComment(commentPostId, commentText.trim())
      setCommentText('')
      const res = await postService.getComments(commentPostId, 0, 20)
      setComments(res.data?.content || res.data || [])
      setPost(prev => prev ? { ...prev, commentCount: (prev.commentCount || 0) + 1 } : prev)
    } catch (e) {
      console.error(e)
    }
    setSubmittingComment(false)
  }, [commentPostId, commentText])

  const handleShare = useCallback(async (postId: number) => {
    try {
      await postService.sharePost(postId)
    } catch (e) {
      console.error(e)
    }
  }, [])

  // Reshare toggle — optimistic flip, then reconcile reshareCount/isReshared
  // from the server response (authoritative), exactly like the feed.
  const handleReshare = useCallback(async (postId: number) => {
    const wasReshared = post?.isReshared || false
    setPost(prev => prev ? {
      ...prev,
      isReshared: !wasReshared,
      reshareCount: wasReshared ? Math.max(0, (prev.reshareCount || 0) - 1) : (prev.reshareCount || 0) + 1,
    } : prev)
    try {
      const res = wasReshared
        ? await postService.unresharePost(postId)
        : await postService.resharePost(postId)
      const updated = res?.data
      setPost(prev => prev ? {
        ...prev,
        isReshared: updated?.isReshared ?? !wasReshared,
        reshareCount: updated?.reshareCount ?? prev.reshareCount,
      } : prev)
    } catch (e) {
      console.error(e)
      // Revert on failure
      setPost(prev => prev ? {
        ...prev,
        isReshared: wasReshared,
        reshareCount: wasReshared ? (prev.reshareCount || 0) + 1 : Math.max(0, (prev.reshareCount || 0) - 1),
      } : prev)
    }
  }, [post])

  const handleDelete = useCallback(async (postId: number) => {
    if (!window.confirm('Delete this post? This cannot be undone.')) return
    try {
      await postService.deletePost(postId)
      navigate(-1)
    } catch (e) {
      console.error(e)
      alert('Failed to delete post.')
    }
  }, [navigate])

  const handleTagClick = useCallback((tag: string) => {
    navigate(`/hashtag/${encodeURIComponent(tag.toLowerCase())}`)
  }, [navigate])

  if (loading) {
    return (
      <GovLayout>
        <div className="max-w-xl mx-auto py-12 flex justify-center">
          <Loader2 className="w-7 h-7 animate-spin text-blue-600" />
        </div>
      </GovLayout>
    )
  }

  if (notFound || !post) {
    return (
      <GovLayout>
        <div className="max-w-xl mx-auto py-12 text-center">
          <div className="text-4xl mb-3">🔍</div>
          <p className="font-medium">Post not found</p>
          <p className="text-sm text-gray-500 mt-1">This post may have been deleted.</p>
          <button onClick={() => navigate('/feed')} className="text-blue-600 text-sm mt-3">Back to feed</button>
        </div>
      </GovLayout>
    )
  }

  return (
    <GovLayout>
      <div className="max-w-xl mx-auto space-y-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        {/* Report — only for posts that are not the viewer's own */}
        {user && post.user.id !== user.id && (
          <div className="flex justify-end -mt-2">
            <button
              onClick={() => setReportPostId(post.id)}
              className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-red-600"
            >
              <Flag className="w-3.5 h-3.5" /> Report post
            </button>
          </div>
        )}

        <PostCard
          post={post}
          onReaction={handleReaction}
          onSave={handleSave}
          onComment={handleOpenComments}
          onShare={handleShare}
          onReshare={handleReshare}
          canDelete={!!user && post.user.id === user.id}
          onDelete={handleDelete}
          onTagClick={handleTagClick}
        />
      </div>

      {/* Comment modal — identical to the feed's */}
      {commentPostId && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4"
          onClick={() => setCommentPostId(null)}
        >
          <div
            className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden shadow-xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-bold">Comments</h2>
              <button onClick={() => setCommentPostId(null)} className="p-1.5 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 max-h-96 overflow-y-auto">
              {loadingComments ? (
                <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto text-blue-600" /></div>
              ) : comments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <MessageCircle className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No comments yet</p>
                  <p className="text-sm">Be the first to comment!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {comments.map((comment: any) => (
                    <div key={comment.id} className="flex gap-3">
                      <img src={comment.user?.profileImage || '/default-avatar.svg'} alt="" className="w-8 h-8 rounded-full object-cover" />
                      <div className="flex-1">
                        <div className="bg-gray-100 rounded-lg px-3 py-2">
                          <p className="font-medium text-sm">{comment.user?.firstName} {comment.user?.lastName}</p>
                          <p className="text-sm text-gray-700">{comment.content}</p>
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                          <span>{comment.createdAt ? new Date(comment.createdAt).toLocaleDateString() : ''}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-gray-100 flex gap-3">
              <img src={user?.profileImage || '/default-avatar.svg'} alt="" className="w-8 h-8 rounded-full object-cover" />
              <div className="flex-1 flex gap-2">
                <input
                  type="text"
                  value={commentText}
                  onChange={e => setCommentText(e.target.value)}
                  placeholder="Write a comment..."
                  className="flex-1 px-4 py-2 bg-gray-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onKeyDown={e => e.key === 'Enter' && handleSubmitComment()}
                />
                <button
                  onClick={handleSubmitComment}
                  disabled={!commentText.trim() || submittingComment}
                  className="px-4 py-2 bg-blue-600 text-white rounded-full text-sm font-medium disabled:opacity-50"
                >
                  {submittingComment ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Post'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Report post modal */}
      {reportPostId && (
        <ReportModal postId={reportPostId} onClose={() => setReportPostId(null)} />
      )}
    </GovLayout>
  )
}

export default PostDetailPage